import { Avatar } from '@advanza/advanza_generic'
import { call } from '@advanza/api'
import { Col, Row } from '@advanza/grid'
import { Icon } from '@advanza/ui'
import { getActiveStatus } from 'components/providers/_providerActiveStatus'
import ProspectLabel from 'components/providers/ProspectLabel'
import A from 'components/ui/A'
import FormattedTime from 'components/ui/FormattedTime'
import HappinessIcon from 'components/ui/HappinessIcon'
import PropTypes from 'prop-types'
import React from 'react'
import { Link } from 'react-router-dom'
import TableList from '../TableList'

class EligibleProvidersList extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            providers: [],
            isFetching: false,
            page: 0,
            count: 0,
            pageSize: 20, // Default page size
        }
        this.renderColumns = this.renderColumns.bind(this)
        this.renderRow = this.renderRow.bind(this)
        this.fetchProviders = this.fetchProviders.bind(this)
        this.onPageChange = this.onPageChange.bind(this)
        this.handleFilterChange = this.handleFilterChange.bind(this)
        this.exportToExcel = this.exportToExcel.bind(this)
    }

    componentDidMount() {
        this.fetchProviders(0)
    }

    fetchProviders(page) {
        const { promoId, modalType = 'eligible' } = this.props
        if (!promoId) {
            return
        }

        this.setState({ isFetching: true })
        const { pageSize } = this.state

        // Use different endpoint based on modal type
        let endpoint
        if (modalType === 'pending' || modalType === 'active') {
            endpoint = `office/promos/get-promo-providers/${promoId}/${modalType}?page=${page}&pageSize=${pageSize}`
        } else {
            endpoint = `office/promos/get-eligible-providers/${promoId}?page=${page}&pageSize=${pageSize}`
        }

        call(endpoint)
            .then((response) => {
                // Handle response structure - items might be nested
                const items = response.items || response.data?.items || []
                const count = response.count || response.data?.count || 0

                this.setState({
                    providers: Array.isArray(items) ? items : [],
                    count: count,
                    page,
                    isFetching: false,
                })
            })
            .catch((error) => {
                console.error(`Failed to fetch ${modalType} providers:`, error)
                this.setState({
                    providers: [],
                    count: 0,
                    isFetching: false,
                })
            })
    }

    componentDidUpdate(prevProps) {
        // Refetch if promoId or modalType changes
        if (
            prevProps.promoId !== this.props.promoId ||
            prevProps.modalType !== this.props.modalType
        ) {
            this.setState({ page: 0 })
            this.fetchProviders(0)
        }
    }

    onPageChange(page) {
        this.setState({ page })
        this.fetchProviders(page)
    }

    handleFilterChange(newFilter) {
        if (newFilter.page !== undefined) {
            this.onPageChange(newFilter.page)
        }
    }

    exportToExcel() {
        const { providers, count } = this.state
        const { modalType = 'eligible', promoId } = this.props
        const typeLabel =
            modalType === 'eligible' ? 'eligible' : modalType === 'pending' ? 'pending' : 'active'

        if (!providers || providers.length === 0) {
            return
        }

        // Define CSV headers
        const headers = [
            'ID',
            'Business Name',
            'Service',
            'City',
            'Sublocality',
            'Prospect Value',
            'Score',
            'Reviews',
            'Created',
            'Status',
            'Active Status',
            'Claimed',
            'Free Trial',
            'Happiness',
        ]

        // Convert providers to CSV rows
        const rows = providers.map((provider) => {
            const proScore = Math.round((provider.total_score || 0) * 100)
            const createdDate = provider.created
                ? new Date(provider.created).toLocaleDateString()
                : ''

            // Get prospect value from office_labels
            let prospectValue = '-'
            if (provider.office_labels && Array.isArray(provider.office_labels)) {
                const prospectLabel = provider.office_labels.find(
                    (label) => label.type === 'prospect',
                )
                if (prospectLabel && prospectLabel.description) {
                    prospectValue = prospectLabel.description
                }
            }

            // Get status description
            const statusMap = {
                0: 'Inactive',
                1: 'Active',
                2: 'Pending',
                3: 'Paused',
                4: 'Deleted',
                5: 'Rejected',
                6: 'Ghost',
                7: 'New',
            }
            const statusText =
                statusMap[provider.status] || `Status ${provider.status || 'Unknown'}`

            return [
                provider.service_provider_id || '',
                provider.business_name || '',
                provider.service_name || provider.service_id || '',
                provider.cityName || '',
                provider.sublocality || '',
                prospectValue,
                proScore,
                provider.nr_ratings || 0,
                createdDate,
                statusText,
                provider.slow_response ? 'Slow Response' : 'Active',
                provider.claimed ? 'Yes' : 'No',
                provider.isFreeTrial ? 'Yes' : 'No',
                provider.happinessTotal || '',
            ]
        })

        // Create CSV content
        const csvContent = [
            headers.join(','),
            ...rows.map((row) =>
                row
                    .map((cell) => {
                        // Escape commas and quotes in cell values
                        const cellValue = String(cell || '')
                        if (
                            cellValue.includes(',') ||
                            cellValue.includes('"') ||
                            cellValue.includes('\n')
                        ) {
                            return `"${cellValue.replace(/"/g, '""')}"`
                        }
                        return cellValue
                    })
                    .join(','),
            ),
        ].join('\n')

        // Create BOM for UTF-8 (helps Excel recognize encoding)
        const BOM = '\uFEFF'
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })

        // Create download link
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute(
            'download',
            `promo-${promoId}-${typeLabel}-providers-${new Date().toISOString().split('T')[0]}.csv`,
        )
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    profileStatus(provider) {
        const map = {
            0: {
                icon: 'power_off',
                color: 'rgb(238, 56, 109)',
            },
            1: {
                icon: 'power',
                color: 'rgb(12, 214, 5)',
            },
            2: {
                icon: 'pending',
                color: 'rgb(244, 101, 17)',
            },
            3: {
                icon: 'pause_circle',
                color: 'rgb(238, 56, 109)',
            },
            4: {
                icon: 'delete_forever',
                color: 'purple',
            },
            5: {
                icon: 'thumb_down_alt',
                color: 'rgb(238, 56, 109)',
            },
            6: {
                icon: '👻',
                color: 'rgb(238, 56, 109)',
            },
            7: {
                icon: 'fiber_new',
                color: 'rgb(244, 101, 17)',
            },
            fallback: {
                icon: 'beach_access',
                color: 'rgb(238,117,230)',
            },
            franchise: {
                icon: 'layers',
                color: 'rgb(100,100,100)',
            },
        }

        if (provider.franchise_status) {
            return map.franchise
        }

        return map[provider.status] || map.fallback
    }

    getUrl(id, slug) {
        if (slug) {
            return `/service/providers/provider/${id}/${slug}`
        }
        return `/service/providers/provider/${id}`
    }

    renderColumns(provider) {
        const proScore = Math.round((provider.total_score || 0) * 100)
        const scoreStyle = {
            color: `hsl(${proScore}, 100%, 40%, 1)`,
        }
        const activeStatus = getActiveStatus(provider)
        const profileStatus = this.profileStatus(provider)

        return [
            <small className="c-grey">
                <Link to={this.getUrl(provider.service_provider_id)}>
                    #{provider.service_provider_id}
                </Link>
            </small>,
            <span>
                {provider.slow_response ? (
                    <i style={{ fontSize: 21 }}>🐢</i>
                ) : (
                    <Icon
                        fontSize={20}
                        name={activeStatus.icon}
                        style={{ color: activeStatus.color }}
                    />
                )}
            </span>,
            <Icon fontSize={20} name={profileStatus.icon} style={{ color: profileStatus.color }} />,
            <A className="classic" to={this.getUrl(provider.service_provider_id)}>
                <Row middle="xs">
                    <Col x>
                        <Avatar sizePx={25} useLetterColor user={provider.user || {}} />
                    </Col>
                    <Col xs>
                        {provider.business_name}{' '}
                        {provider.claimed && <Icon name="verified_user" fontSize={16} primColor />}
                        {provider.isFreeTrial && <Icon name="explosion" fontSize={16} green />}
                        <small className="text">{provider.sublocality}</small>
                    </Col>
                </Row>
            </A>,
            provider.service_name || provider.service_id,
            <small className="c-grey">{provider.cityName}</small>,
            <small>
                <ProspectLabel providerId={provider.service_provider_id} provider={provider} />
            </small>,
            <span style={scoreStyle}>{proScore}</span>,
            <small>{provider.nr_ratings || 0}</small>,
            <small className="text">
                <FormattedTime date={provider.created} />
            </small>,
            provider.isSigned && <HappinessIcon happiness={provider.happinessTotal} />,
            <Link to={this.getUrl(provider.service_provider_id, 'company')}>
                <Icon name="settings" />
            </Link>,
        ]
    }

    renderRow(provider) {
        const { backgroundColor } = getActiveStatus(provider)
        const providerUrl = this.getUrl(provider.service_provider_id)
        return {
            url: providerUrl,
            cols: this.renderColumns(provider),
            backgroundColor,
            id: provider.service_provider_id,
            // Add onClick handler to open in new tab
            onClick: (e) => {
                // Check if Ctrl/Cmd key is pressed or if it's a middle click
                if (e.ctrlKey || e.metaKey || e.button === 1) {
                    // Let default behavior handle it (opens in new tab)
                    return
                }
                // Prevent default navigation and open in new tab
                e.preventDefault()
                e.stopPropagation()
                window.open(providerUrl, '_blank', 'noopener,noreferrer')
            },
        }
    }

    definitions = {
        id: { name: '#', preventRedirect: true },
        ActiveStatus: { name: '>' },
        status: { name: '*' },
        name: { name: 'name' },
        service: { name: 'service' },
        city: { name: 'city' },
        prospect: { name: '€' },
        score: { name: 'score' },
        reviews: { name: <Icon fontSize={16} name="stars" /> },
        time: { name: 'time' },
        happiness: { name: <Icon fontSize={16} name="sentiment_satisfied" /> },
        linkToEdit: { name: <Icon name="settings" fontSize={16} />, preventRedirect: true },
    }

    render() {
        const { providers, isFetching, count, page } = this.state

        // Convert providers array to format expected by TableList
        // TableList expects: pages = { [pageNr]: [id1, id2, ...] }, renderRow function
        // Filter out any providers without a valid ID and remove duplicates
        // Note: We filter duplicates but the count should reflect the actual items returned
        const validProviders = providers.filter((p) => p && p.service_provider_id)
        const providerIds = [...new Set(validProviders.map((p) => p.service_provider_id))]
        const pages = { [page]: providerIds }

        // Use the actual count of items returned, not the filtered count
        // This ensures pagination works correctly
        const actualItemCount = validProviders.length

        // Create rows object for TableList (optional, renderRow will be used instead)
        const rows = {}
        validProviders.forEach((provider) => {
            if (provider.service_provider_id) {
                rows[provider.service_provider_id] = this.renderRow(provider)
            }
        })

        // Create filter object for TableList
        const filter = { page }

        const { modalType = 'eligible' } = this.props
        const typeLabel =
            modalType === 'eligible' ? 'eligible' : modalType === 'pending' ? 'pending' : 'active'

        return (
            <div>
                <div
                    style={{
                        marginBottom: '16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                    <div>
                        <strong>
                            Total {typeLabel} providers: {count}
                        </strong>
                        {count > providerIds.length && (
                            <span style={{ marginLeft: '10px', color: '#666', fontSize: '14px' }}>
                                (Showing {providerIds.length} of {count})
                            </span>
                        )}
                    </div>
                    {!isFetching && providerIds.length > 0 && (
                        <button
                            onClick={this.exportToExcel}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '14px',
                            }}
                            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#45a049')}
                            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#4CAF50')}>
                            <Icon name="file_download" fontSize={16} />
                            Export to Excel
                        </button>
                    )}
                </div>
                {isFetching && (
                    <div style={{ padding: '20px', textAlign: 'center' }}>Loading providers...</div>
                )}
                {!isFetching && providerIds.length === 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        No {typeLabel} providers found
                    </div>
                )}
                {!isFetching && providerIds.length > 0 && (
                    <TableList
                        pages={pages}
                        filter={{
                            page: this.state.page,
                            pageSize: this.state.pageSize,
                        }}
                        changeFilter={this.handleFilterChange}
                        rows={rows}
                        renderRow={(id) =>
                            rows[id] ||
                            this.renderRow(providers.find((p) => p.service_provider_id === id))
                        }
                        definitions={this.definitions}
                        isFetching={isFetching}
                        count={count}
                        addListFeatures
                        hideSearch
                        hideExport
                        hideOrder
                        hidePagination={false}
                    />
                )}
            </div>
        )
    }
}

EligibleProvidersList.propTypes = {
    promoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    modalType: PropTypes.oneOf(['eligible', 'pending', 'active']),
}

export default EligibleProvidersList
